import { tutorialAssets } from "assets/cdn";
import { useImagePath } from "hooks/useImagePath";
import { FC, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTutorialAnchor } from "../../hooks";
import { TutorialV2StepProps } from "../../types";
import {
	Arrow,
	ArrowDownContainer,
	Container,
	Content,
	NextButton,
	Slide,
	SlideNumber,
	Subtitle,
	Tiki,
	Title
} from "../styles";
import { ButtonShine, ButtonShineLayer } from "./styles";

const FIRST_ROW_TOLERANCE = 2;
const ARROW_CENTER_EM = 25.8;

const getFirstRowRightmostOffer = (): HTMLElement | null => {
	const offers = Array.from(
		document.querySelectorAll<HTMLElement>("[data-tutorial-single-item-offer]")
	)
		.map((element) => ({ element, rect: element.getBoundingClientRect() }))
		.filter(({ rect }) => rect.width > 0 && rect.height > 0);

	if (!offers.length) {
		return null;
	}

	const firstRowTop = Math.min(...offers.map(({ rect }) => rect.top));
	const firstRowOffers = offers.filter(
		({ rect }) => Math.abs(rect.top - firstRowTop) <= FIRST_ROW_TOLERANCE
	);

	return firstRowOffers.reduce((rightmostOffer, offer) =>
		offer.rect.right > rightmostOffer.rect.right ? offer : rightmostOffer
	).element;
};

export const Step3: FC<TutorialV2StepProps> = ({
	currentStep,
	totalSteps,
	onNext
}) => {
	const { getImage } = useImagePath("cdn");
	const background = getImage(tutorialAssets.background);
	const ArrowImage = getImage(tutorialAssets.greenArrow);
	const ShineImage = getImage(tutorialAssets.shine);
	const TikiImage = getImage(tutorialAssets.char2);
	const { targetElement, targetRect } = useTutorialAnchor({
		panelAnchorOffsetEm: 5.5,
		panelPlacement: "below",
		panelSelector: "[data-tutorial-v2-step3-panel]",
		scrollMode: "tutorial-offer-side",
		targetResolver: getFirstRowRightmostOffer
	});
	const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
	const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
	const isCompactLandscape =
		viewportWidth > viewportHeight && viewportHeight <= 500;
	const panelPlacement = isCompactLandscape ? "left" : "below";
	const panelAnchorTop = isCompactLandscape
		? targetRect
			? targetRect.top + targetRect.height / 2
			: undefined
		: targetRect?.bottom;
	const purchaseButton = targetElement?.querySelector<HTMLElement>(
		"button[id$=-button]"
	);
	const purchaseButtonRect = targetRect
		? purchaseButton?.getBoundingClientRect()
		: null;

	useEffect(() => {
		if (!targetElement) {
			return;
		}

		const previousPosition = targetElement.style.position;
		const previousZIndex = targetElement.style.zIndex;
		const targetButton = targetElement.querySelector<HTMLElement>(
			"button[id$=-button]"
		);
		const previousButtonPosition = targetButton?.style.position ?? "";
		const previousButtonZIndex = targetButton?.style.zIndex ?? "";
		targetElement.style.position = targetElement.style.position || "relative";
		targetElement.style.zIndex = "10001";
		if (targetButton) {
			targetButton.style.position = "relative";
			targetButton.style.zIndex = "1";
		}

		return () => {
			targetElement.style.position = previousPosition;
			targetElement.style.zIndex = previousZIndex;
			if (targetButton) {
				targetButton.style.position = previousButtonPosition;
				targetButton.style.zIndex = previousButtonZIndex;
			}
			if (!targetElement.getAttribute("style")) {
				targetElement.removeAttribute("style");
			}
		};
	}, [targetElement]);

	return (
		<>
			{targetElement &&
				purchaseButtonRect &&
				createPortal(
					<ButtonShineLayer>
						<ButtonShine
							$left={purchaseButtonRect.left + purchaseButtonRect.width / 2}
							$top={purchaseButtonRect.top + purchaseButtonRect.height / 2}
							src={ShineImage}
						/>
					</ButtonShineLayer>,
					targetElement
				)}
			<Container
				$anchorCenterOffset={targetRect ? targetRect.width / 2 : 0}
				$anchorLeft={targetRect?.left}
				$anchorOffset={5.5}
				$anchorTop={panelAnchorTop}
				$arrowCenter={ARROW_CENTER_EM}
				$placement={panelPlacement}
				data-tutorial-v2-step3-panel
			>
				<ArrowDownContainer
					className={isCompactLandscape ? "right" : undefined}
					$right={5}
				>
					<Arrow
						className={isCompactLandscape ? "right" : undefined}
						src={ArrowImage}
					/>
				</ArrowDownContainer>
				<SlideNumber>
					{currentStep}/{totalSteps}
				</SlideNumber>
				<Slide $backgroundImage={background}>
					<Tiki src={TikiImage} />
					<Content>
						<Title>Shop with Confidence</Title>
						<Subtitle>
							Purchases are made securely
							<br /> through the Official Tiki Solitaire
							<br /> TriPeaks Web Shop.
						</Subtitle>
					</Content>
					<NextButton onClick={onNext}>Got It</NextButton>
				</Slide>
			</Container>
		</>
	);
};
